import React, { useRef, useState } from 'react';
import { FaCamera, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { authService } from '../../../../services/apiService';

export default function ExtractionAcceptModal({ order, onClose, onAccepted }) {
  const inputRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await authService.uploadCompressedFile(file);
      setPhotoUrl(res?.path || res?.url || res);
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!photoUrl) {
      toast.error('Take a post-extraction photo first');
      return;
    }
    setSubmitting(true);
    try {
      await onAccepted({ extractionPhotoUrl: photoUrl });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-slate-900">Confirm part extraction</h3>
        <p className="text-sm text-slate-600">
          This order is <strong>{order?.raw?.availabilityTier || 'Likely/Buyer-requested'}</strong> tier.
          Extract the part from the vehicle and photograph it before accepting.
        </p>
        <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside">
          <li>Physically remove the part from the source vehicle</li>
          <li>Take a clear post-extraction photo</li>
          <li>Confirm — buyer receives &quot;Part extraction confirmed.&quot;</li>
        </ol>
        {photoUrl ? (
          <img src={photoUrl} alt="Extraction" className="w-full max-h-48 object-cover rounded-lg border" />
        ) : null}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full py-3 rounded-xl border-2 border-dashed border-emerald-400 text-emerald-700 font-semibold flex items-center justify-center gap-2"
        >
          {uploading ? <FaSpinner className="animate-spin" /> : <FaCamera />}
          {photoUrl ? 'Retake photo' : 'Capture extraction photo'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <p className="text-[10px] text-slate-500">
          Cannot extract? Cancel the order instead — SLA breach rules apply.
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border text-sm font-medium">
            Back
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !photoUrl}
            className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
            Accept order
          </button>
        </div>
      </div>
    </div>
  );
}
